/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";

dayjs.extend(utc);
dayjs.extend(timezone);

const ALL_VALUE = "All";

// Restaurant operations run on Central Time. Both the calendar day used to
// build the presets below AND the time_zone label sent to the backend must
// describe the same zone — otherwise the backend (which interprets date_range
// using the given time_zone) reads a day that hasn't started/already ended
// there, rather than the "today" a Central-zoned admin actually sees.
export const REPORTING_TIME_ZONE = "America/Chicago";

// Custom Date values are plain calendar dates the operator picked (or that we
// synthesize on their behalf, e.g. auto-filling "To") — they carry no
// timezone metadata and are sent as-is. This returns "today" as that same
// kind of plain calendar date, but computed in the reporting timezone so an
// auto-filled "To" matches the Central day the label describes rather than
// the browser's local day.
export const getReportingToday = (now = dayjs()) =>
  dayjs(now.tz(REPORTING_TIME_ZONE).format("YYYY-MM-DD"));

export const CUSTOM_DATE = "Custom Date";

export const DATE_FILTER_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last 7 Days",
  "This Month",
  "Last Month",
  CUSTOM_DATE,
];

// Next filters after one date control changes: a preset drops the custom dates,
// a From date fills an empty To with today, and clearing From clears To.
export const applyDateFilterChange = (prev, field, value) => {
  const nextFilters = { ...prev, [field]: value };

  if (field === "dateFilter" && value !== CUSTOM_DATE) {
    nextFilters.customDateFrom = null;
    nextFilters.customDateTo = null;
  }

  if (field === "customDateFrom") {
    if (!value) {
      nextFilters.customDateTo = null;
    } else if (isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)) {
      nextFilters.customDateTo = getReportingToday();
    }
  }

  return nextFilters;
};

export const isCustomDateRangeInvalid = (filters) =>
  filters?.dateFilter === CUSTOM_DATE &&
  !isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

export const buildEmployeeDiscountQueryFilters = (
  filters,
  { now = dayjs(), timeZone = REPORTING_TIME_ZONE, search } = {},
) => {
  const params = {};
  const zonedNow = now.tz(timeZone);

  if (filters?.dateFilter !== "Custom Date" && filters?.dateFilter !== ALL_VALUE) {
    switch (filters?.dateFilter) {
      case "Today":
        params.date_range = {
          from: zonedNow.format("DD/MM/YYYY"),
          to: zonedNow.format("DD/MM/YYYY"),
        };
        break;
      case "Last 7 Days":
        params.date_range = {
          from: zonedNow.subtract(7, "day").format("DD/MM/YYYY"),
          to: zonedNow.format("DD/MM/YYYY"),
        };
        break;
      case "Yesterday":
        params.date_range = {
          from: zonedNow.subtract(1, "day").format("DD/MM/YYYY"),
          to: zonedNow.subtract(1, "day").format("DD/MM/YYYY"),
        };
        break;
      case "This Week":
        params.date_range = {
          from: zonedNow.startOf("week").format("DD/MM/YYYY"),
          to: zonedNow.endOf("week").format("DD/MM/YYYY"),
        };
        break;
      case "This Month":
        params.date_range = {
          from: zonedNow.startOf("month").format("DD/MM/YYYY"),
          to: zonedNow.endOf("month").format("DD/MM/YYYY"),
        };
        break;
      case "Last Month":
        params.date_range = {
          from: zonedNow.subtract(1, "month").startOf("month").format("DD/MM/YYYY"),
          to: zonedNow.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
        };
        break;
      default:
        break;
    }
  } else if (filters?.dateFilter === "Custom Date" && isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo)) {
    params.date_range = {
      from: dayjs(filters?.customDateFrom).format("DD/MM/YYYY"),
      to: dayjs(filters?.customDateTo).format("DD/MM/YYYY"),
    };
  }

  if (params.date_range && timeZone) {
    params.time_zone = timeZone;
  }

  if (
    filters?.restaurant_group_id &&
    filters?.restaurant_group_id !== ALL_VALUE
  ) {
    params.restaurant_group_id = filters?.restaurant_group_id;
  }

  if (
    filters?.restaurant_sub_group_id &&
    filters?.restaurant_sub_group_id !== ALL_VALUE
  ) {
    params.restaurant_sub_group_id = filters?.restaurant_sub_group_id;
  }

  if (
    Array.isArray(filters?.restaurant_ids) &&
    !filters?.restaurant_ids?.includes(ALL_VALUE)
  ) {
    params.restaurant_ids = filters?.restaurant_ids;
  }

  if (filters?.network_guest_ids && filters?.network_guest_ids !== ALL_VALUE) {
    params.network_guest_ids = filters?.network_guest_ids;
  }

  if (filters?.order_status && filters?.order_status !== ALL_VALUE) {
    params.order_status = filters?.order_status === "Reconciled";
  }

  if (search) {
    params.search = search;
  }

  return params;
};
